'use client';

import clsx from 'clsx';
import Link from 'next/link';
import Image from 'next/image';
import type { ListingItemTypes } from '@/types';
import { ChevronRightIcon } from '@/components/icons/chevronRight';
import { ChevronLeftIcon } from '@/components/icons/chevronLeft';
import AddToWishlist from '@/components/add-to-wishlist';
import ActionIcon from '@/components/ui/action-icon';
import Rate from '@/components/ui/rating';
import { Routes } from '@/config/routes';
import React, { useState, useEffect } from 'react';

export default function ListingCard({
  id,
  type,
  slides,
  caption,
  title,
  slug,
  location,
  price,
  priceh,
  rating,
  ratingCount,
}: ListingItemTypes) {
  const [locale, setLocale] = useState('en-US');
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    // Get browser language preference
    const userLocale = navigator.language || 'en-US';
    setLocale(userLocale);

    // Get user's time zone
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Map time zones to correct currencies
    const timeZoneCurrencyMap = {
      'Australia/Sydney': 'AUD',
      'Australia/Melbourne': 'AUD',
      'Australia/Brisbane': 'AUD',
      'Australia/Perth': 'AUD',
      'Australia/Adelaide': 'AUD',
      'Australia/Darwin': 'AUD',
      'America/New_York': 'USD',
      'America/Los_Angeles': 'USD',
      'Europe/London': 'GBP',
      'Europe/Berlin': 'EUR',
      'Asia/Tokyo': 'JPY',
      'Canada/Toronto': 'CAD',
    };

    // Get currency based on time zone or default to USD
    setCurrency(timeZoneCurrencyMap[timeZone] || 'USD');
  }, []);

  // Function to format prices dynamically
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <>
      <div className="listing-card group/item relative inline-flex w-full flex-col">
        <div className="relative w-full overflow-hidden rounded-xl">
          <AddToWishlist
            isWishListed={false}
            onClick={(data) => console.log('Item added to Wishlist.', data)}
          />
          <Link href={Routes.public.listingDetails(slug)}>
            <div className="listing-item after:absolute after:bottom-0 after:left-0 after:z-[1] after:h-1/4 after:w-full after:bg-gradient-to-t after:from-black/25">
              <Image
                className="aspect-[34/25] bg-gray-lighter"
                src={
                  Array.isArray(slides) ? slides[0] : slides || '/fallback.jpg'
                }
                width={816}
                height={600}
                alt="boat"
                priority
              />
              <ActionIcon
                rounded="full"
                color="light"
                size="sm"
                className={clsx(
                  'absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 shadow-md !transition-all focus:!ring-0 md:invisible md:flex md:disabled:hidden md:group-hover/item:visible',
                  `${id}-listing-item-button-prev`,
                )}
              >
                <ChevronLeftIcon className="-ml-0.5 h-auto w-[7px]" />
              </ActionIcon>
              <ActionIcon
                rounded="full"
                size="sm"
                color="light"
                className={clsx(
                  'absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 opacity-80 shadow-md !transition-all duration-300 focus:!ring-0 md:invisible md:flex md:disabled:hidden md:group-hover/item:visible md:group-hover/item:opacity-100',
                  `${id}-listing-item-button-next`,
                )}
              >
                <ChevronRightIcon className="-mr-0.5 h-auto w-[7px]" />
              </ActionIcon>
            </div>
          </Link>
        </div>
        <Link href={Routes.public.listingDetails(slug)}>
          <div className="content pt-3">
            <div className="mb-1 flex items-center gap-5">
              <span className="font-bold">{caption}</span>
            </div>
            <h4 className="text-ellipsis text-gray-dark 2xl:mb-1.5">{title}</h4>
            <p className="mb-3 text-gray-light xl:mb-3">{location}</p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              {price && Number(price) !== 0 ? (
                <p className="text-gray-light">
                  <span className="font-bold text-gray-dark xl:text-[18px] 3xl:text-xl">
                    {formatPrice(price)}
                  </span>{' '}
                  a day
                </p>
              ) : null}

              {priceh && Number(priceh) !== 0 ? (
                <p className="text-gray-light">
                  <span className="font-bold text-gray-dark xl:text-[18px] 3xl:text-xl">
                    {formatPrice(priceh)}
                  </span>{' '}
                  an hour
                </p>
              ) : null}
            </div>
          </div>
        </Link>
      </div>
    </>
  );
}
